import { useEffect, useState } from "react";
import { api, formatBRL, formatPerWeight, WEIGHT_UNITS } from "@/lib/api";
import { Users, Storefront, Anchor } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import UnitToggle from "@/components/UnitToggle";

export default function Players() {
  const { unit, settings } = useSettings();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/competitors").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const unitLabel = WEIGHT_UNITS.find((u) => u.value === unit)?.label || "100g";
  const anchoredSet = new Set((settings.anchored_channels || []).map((c) => c.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="players-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Players de Mercado</div>
          <h1 className="h-display" style={{ color: "var(--text-primary)" }}>
            Mapa de Concorrência
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Vendedores e marcas detectados automaticamente a partir das ofertas online dos produtos rastreados.
          </p>
        </div>
        <UnitToggle />
      </header>

      <section className="surface">
        <div className="px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border-default)" }}>
          <Users size={18} weight="duotone" style={{ color: "var(--gold)" }} />
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>
            {rows.length} players identificados
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>Carregando…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>
            Nenhum player detectado ainda. Rastreie alguns produtos primeiro.
          </div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--text-secondary)" }}>
                  <th className="px-6 py-3 label-eyebrow">Vendedor / Marca</th>
                  <th className="px-6 py-3 label-eyebrow">Canal</th>
                  <th className="px-6 py-3 label-eyebrow">Ofertas</th>
                  <th className="px-6 py-3 label-eyebrow">Mín. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Méd. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Máx. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Preço médio</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const isAnchor = anchoredSet.has((r.channel || "").toLowerCase());
                  return (
                    <tr key={`${r.seller}-${i}`} className="border-t surface-hover" style={{ borderColor: "var(--border-default)" }} data-testid={`player-row-${i}`}>
                      <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{r.seller || "—"}</td>
                      <td className="px-6 py-3">
                        <span
                          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 inline-flex items-center gap-1"
                          style={{
                            background: isAnchor ? "rgba(212,175,55,0.08)" : "var(--bg-surface-hover)",
                            color: isAnchor ? "var(--gold)" : "var(--text-secondary)",
                            border: `1px solid ${isAnchor ? "rgba(212,175,55,0.2)" : "var(--border-default)"}`,
                          }}
                        >
                          {isAnchor ? <Anchor size={9} weight="fill" /> : <Storefront size={9} />}
                          {r.channel || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-3" style={{ color: "var(--text-secondary)" }}>{r.products_count}</td>
                      <td className="px-6 py-3" style={{ color: "var(--success)" }}>{formatPerWeight(r.min_norm, unit)}</td>
                      <td className="px-6 py-3" style={{ color: "var(--gold)" }}>{formatPerWeight(r.avg_norm, unit)}</td>
                      <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{formatPerWeight(r.max_norm, unit)}</td>
                      <td className="px-6 py-3" style={{ color: "var(--text-secondary)" }}>{formatBRL(r.avg_price)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
